import { z } from 'zod';
import { activeTotalsSchema } from '@/api/listTotals';
import { paginationMetaSchema } from '@/api/pagination';
import {
  INSTITUTE_LIST_VARIANTS,
  INSTITUTE_PAPERS_ORIGIN_PATHS,
  MONITOR_SCREEN_PATH,
  MONITOR_SCREEN_PATHS,
  MONITOR_TAB_IDS,
  PROJECT_MANAGEMENT_PATH,
  PROJECT_TAB_IDS,
  type MonitorScreenPath,
} from '../constants/question.constants';

export const monitorRowSchema = z.object({
  groupId: z.coerce.number().nullable(),
  groupName: z.string().nullable(),
  isTpOtherMenu: z.boolean().nullable(),
  username: z.string().nullable(),
  userMobile: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  memberCount: z.number(),
  instituteCount: z.number(),
  assignedCount: z.number(),
  collectedCount: z.number(),
  newCount: z.number(),
});

export const monitorResponseSchema = z.object({
  data: z.array(monitorRowSchema),
  meta: paginationMetaSchema,
});

export const monitorDetailRowSchema = z.object({
  projectInstituteId: z.coerce.number(),
  projectId: z.coerce.number().nullable(),
  userId: z.coerce.number().nullable(),
  userName: z.string().nullable(),
  userMobile: z.string().nullable(),
  userHrEmpId: z.string().nullable(),
  userDesignation: z.string().nullable(),
  userThanaName: z.string().nullable(),
  userDistrictName: z.string().nullable(),
  userDivisionName: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  isTpOtherMenu: z.boolean().nullable(),
  boardName: z.string().nullable(),
  className: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  assignedCount: z.number(),
  collectedCount: z.number(),
  newCount: z.number(),
  assignedAt: z.string().nullable(),
});

export const monitorDetailResponseSchema = z.object({
  data: z.array(monitorDetailRowSchema),
  meta: paginationMetaSchema,
});

export const questionProjectSchema = z.object({
  projectId: z.coerce.number(),
  name: z.string(),
  classId: z.coerce.number().nullable(),
  className: z.string().nullable(),
  declareDate: z.string().nullable(),
  deadlineDate: z.string().nullable(),
  isActive: z.boolean(),
  instituteCount: z.number(),
  pendingCount: z.number(),
});

export const questionProjectResponseSchema = z.object({
  data: z.array(questionProjectSchema),
  meta: paginationMetaSchema.extend({
    totals: activeTotalsSchema.extend({ institutes: z.number() }),
  }),
});

export const instituteSubjectSchema = z.object({
  instituteSubjectId: z.coerce.number(),
  subjectId: z.coerce.number().nullable(),
  subjectName: z.string().nullable(),
  subjectNameLocal: z.string().nullable(),
  subjectCode: z.string().nullable(),
  targetDate: z.string().nullable(),
  downloadCount: z.number(),
  isCollected: z.boolean(),
  isNew: z.boolean(),
  collectedAt: z.string().nullable(),
  uploadId: z.coerce.number().nullable(),
  fileName: z.string().nullable(),
  fileExt: z.string().nullable(),
  uploadedAt: z.string().nullable(),
  viewCount: z.number(),
  hasFile: z.boolean(),
});

export const instituteSubjectListSchema = z.array(instituteSubjectSchema);

export const questionClassSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
  nameLocal: z.string().nullable(),
  description: z.string().nullable(),
  slNum: z.coerce.number().nullable(),
  isActive: z.boolean(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  subjectCount: z.number(),
});

export const questionClassListSchema = z.array(questionClassSchema);

export const questionSubjectSchema = z.object({
  id: z.coerce.number(),
  legacyId: z.coerce.number(),
  code: z.string().nullable(),
  name: z.string(),
  nameLocal: z.string().nullable(),
  description: z.string().nullable(),
  slNum: z.coerce.number().nullable(),
  isActive: z.boolean(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
});

export const questionSubjectResponseSchema = z.object({
  data: z.array(questionSubjectSchema),
  meta: paginationMetaSchema,
});

export const classSubjectSchema = z.object({
  classSubjectId: z.coerce.number(),
  classId: z.coerce.number(),
  className: z.string(),
  subjectId: z.coerce.number(),
  subjectName: z.string(),
  subjectCode: z.string().nullable(),
  subjectLocal: z.string().nullable(),
  isActive: z.boolean(),
});

export const classSubjectListSchema = z.array(classSubjectSchema);

export type MonitorRow = z.infer<typeof monitorRowSchema>;
export type MonitorResponse = z.infer<typeof monitorResponseSchema>;
export type MonitorDetailRow = z.infer<typeof monitorDetailRowSchema>;
export type MonitorDetailResponse = z.infer<typeof monitorDetailResponseSchema>;
export type QuestionProject = z.infer<typeof questionProjectSchema>;
export type QuestionProjectResponse = z.infer<typeof questionProjectResponseSchema>;
export type InstituteSubject = z.infer<typeof instituteSubjectSchema>;
export type QuestionClass = z.infer<typeof questionClassSchema>;
export type QuestionSubject = z.infer<typeof questionSubjectSchema>;
export type QuestionSubjectResponse = z.infer<typeof questionSubjectResponseSchema>;
export type ClassSubject = z.infer<typeof classSubjectSchema>;

const numberList = z.preprocess(
  (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
  z.array(z.coerce.number()),
);

export const monitorSearchSchema = z.object({
  projectId: z.coerce.number().optional(),
  isNew: z.boolean().optional(),
  collected: z.boolean().optional(),
  userId: z.coerce.number().optional(),
  instituteId: z.coerce.number().optional(),
  boardId: z.coerce.number().optional(),
  targetDate: z.string().optional(),
  subjectIds: numberList.optional(),
  classId: z.coerce.number().optional(),
  zillaId: z.coerce.number().optional(),
  isTpOtherMenu: z.boolean().optional(),
  search: z.string().optional(),
});

export const monitorPageSearchSchema = monitorSearchSchema.extend({
  tab: z.enum(MONITOR_TAB_IDS).optional().catch(undefined),
});

export const SUBJECT_LIST_TYPES = ['A', 'C'] as const;

export type SubjectListType = (typeof SUBJECT_LIST_TYPES)[number];

const monitorOriginSchema = (defaultPath: MonitorScreenPath) =>
  z.enum(MONITOR_SCREEN_PATHS).catch(defaultPath);

export const monitorClassesSearchSchema = monitorSearchSchema.extend({
  boardId: z.coerce.number(),
  from: monitorOriginSchema(MONITOR_SCREEN_PATH.byClass),
});

export const monitorDistrictsSearchSchema = monitorSearchSchema.extend({
  boardId: z.coerce.number(),
  from: monitorOriginSchema(MONITOR_SCREEN_PATH.byBoard),
});

export const monitorInstitutesSearchSchema = monitorSearchSchema.extend({
  variant: z.enum(INSTITUTE_LIST_VARIANTS).catch('byArea'),
  from: monitorOriginSchema(MONITOR_SCREEN_PATH.member),
});

export const monitorSubjectsSearchSchema = z.object({
  subjectType: z.enum(SUBJECT_LIST_TYPES).catch('A'),
  isNew: z.boolean().optional().catch(undefined),
  projectId: z.coerce.number().optional().catch(undefined),
  userId: z.coerce.number().optional().catch(undefined),
  instituteId: z.coerce.number().optional().catch(undefined),
  from: monitorOriginSchema(MONITOR_SCREEN_PATH.member),
});

export const institutePapersSearchSchema = z.object({
  from: z.enum(INSTITUTE_PAPERS_ORIGIN_PATHS).catch(PROJECT_MANAGEMENT_PATH),
  projectId: z.coerce.number().optional().catch(undefined),
  userId: z.coerce.number().optional().catch(undefined),
  instituteId: z.coerce.number().optional().catch(undefined),
  isCollectedOnly: z.boolean().optional().catch(undefined),
});

export type InstitutePapersSearch = z.infer<typeof institutePapersSearchSchema>;

export type MonitorBoardBreakdownSearch = z.infer<typeof monitorClassesSearchSchema>;
export type MonitorInstitutesSearch = z.infer<typeof monitorInstitutesSearchSchema>;
export type MonitorSubjectsSearch = z.infer<typeof monitorSubjectsSearchSchema>;

export interface InstituteSubjectQuery {
  subjectType?: SubjectListType;
  isNew?: boolean;
}

export const projectSearchSchema = z.object({
  search: z.string().optional(),
  classId: z.coerce.number().optional(),
  isActive: z.boolean().optional(),
});

export const projectManagementSearchSchema = projectSearchSchema.extend({
  tab: z.enum(PROJECT_TAB_IDS).optional().catch(undefined),
  projectId: z.coerce.number().optional().catch(undefined),
  userId: z.coerce.number().optional().catch(undefined),
});

export const uploadSearchSchema = z.object({
  projectId: z.coerce.number().optional(),
  projectInstituteId: z.coerce.number().optional(),
  instituteId: z.coerce.number().optional(),
  instituteName: z.string().optional(),
  instituteCode: z.string().optional(),
  instituteEiin: z.string().optional(),
  divisionName: z.string().optional(),
  districtName: z.string().optional(),
  thanaName: z.string().optional(),
});

export const subjectSearchSchema = z.object({
  classId: z.coerce.number().optional(),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
});

export type MonitorSearch = z.infer<typeof monitorSearchSchema>;
export type ProjectSearch = z.infer<typeof projectSearchSchema>;
export type ProjectManagementSearch = z.infer<typeof projectManagementSearchSchema>;
export type UploadSearch = z.infer<typeof uploadSearchSchema>;
export type SubjectSearch = z.infer<typeof subjectSearchSchema>;
export type SubjectListSearch = Pick<SubjectSearch, 'search' | 'isActive'>;

const NAME_MAX = 100;
const CODE_MAX = 20;
const SL_NUM_MAX = 9999;

const optionalWholeNumber = (label: string, max: number) =>
  z
    .string()
    .trim()
    .refine((value) => value === '' || /^\d+$/u.test(value), {
      message: `${label} must be a whole number`,
    })
    .refine((value) => value === '' || Number(value) <= max, {
      message: `${label} must be ${String(max)} or less`,
    });

const activeWindowOrdered = <T extends { activeFrom: string; activeTo: string }>(
  values: T,
  ctx: z.RefinementCtx,
): void => {
  if (values.activeFrom === '' || values.activeTo === '') return;
  if (values.activeTo >= values.activeFrom) return;
  ctx.addIssue({
    code: 'custom',
    path: ['activeTo'],
    message: 'Active To cannot be earlier than Active From',
  });
};

export const questionClassFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(NAME_MAX),
    code: optionalWholeNumber('Code', 999_999),
    nameLocal: z.string().trim().max(NAME_MAX),
    description: z.string().trim().max(1000),
    slNum: optionalWholeNumber('Sl Num', SL_NUM_MAX),
    isActive: z.boolean(),
    activeFrom: z.string().trim(),
    activeTo: z.string().trim(),
  })
  .superRefine(activeWindowOrdered);

export const questionSubjectFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(NAME_MAX),
    code: z.string().trim().max(CODE_MAX),
    nameLocal: z.string().trim().max(NAME_MAX),
    description: z.string().trim().max(1000),
    slNum: optionalWholeNumber('Sl Num', SL_NUM_MAX),
    isActive: z.boolean(),
    activeFrom: z.string().trim(),
    activeTo: z.string().trim(),
  })
  .superRefine(activeWindowOrdered);

export type QuestionClassFormValues = z.infer<typeof questionClassFormSchema>;
export type QuestionSubjectFormValues = z.infer<typeof questionSubjectFormSchema>;

export interface QuestionClassPayload {
  name: string;
  code?: number;
  nameLocal?: string;
  description?: string;
  slNum?: number;
  isActive: boolean;
  activeFrom?: string;
  activeTo?: string;
}

export interface QuestionSubjectPayload {
  activeFrom?: string;
  activeTo?: string;
  name: string;
  code?: string;
  nameLocal?: string;
  description?: string;
  slNum?: number;
  isActive: boolean;
}

export const assignmentSchema = z.object({
  projectInstituteId: z.coerce.number(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  assignedCount: z.number(),
  collectedCount: z.number(),
  assignedAt: z.string(),
});

export const assignmentListSchema = z.array(assignmentSchema);

export const uploadSubjectSchema = z.object({
  instituteSubjectId: z.coerce.number(),
  subjectId: z.coerce.number().nullable(),
  subjectName: z.string().nullable(),
  subjectLocal: z.string().nullable(),
  targetDate: z.string().nullable(),
  uploadCount: z.number(),
});

export const uploadSubjectListSchema = z.array(uploadSubjectSchema);

export const paperSchema = z.object({
  id: z.coerce.number(),
  fileName: z.string().nullable(),
  fileExt: z.string().nullable(),
  fileType: z.string().nullable(),
  uploadedAt: z.string(),
  uploadedBy: z.string().nullable(),
  hasFile: z.boolean(),
});

export const paperListSchema = z.array(paperSchema);

export type Assignment = z.infer<typeof assignmentSchema>;
export type UploadSubject = z.infer<typeof uploadSubjectSchema>;
export type Paper = z.infer<typeof paperSchema>;

export const questionProjectFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(1000),
    classId: z.number().optional(),
    declareDate: z.string().optional(),
    deadlineDate: z.string().optional(),
    isActive: z.boolean(),
  })
  .refine(
    (value) =>
      value.declareDate === undefined ||
      value.deadlineDate === undefined ||
      value.deadlineDate >= value.declareDate,
    {
      message: 'The deadline cannot fall before the declare date',
      path: ['deadlineDate'],
    },
  );

export type QuestionProjectFormValues = z.infer<typeof questionProjectFormSchema>;

export interface QuestionProjectPayload {
  name: string;
  classId?: number;
  declareDate?: string;
  deadlineDate?: string;
  isActive: boolean;
}

export interface AssignedSubjectPayload {
  subjectId: number;
  targetDate?: string;
}

export interface CreateAssignmentPayload {
  userId: number;
  instituteId: number;
  subjects: AssignedSubjectPayload[];
}

export const memberInstituteSchema = z.object({
  instituteId: z.coerce.number(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
});

export const memberInstituteListSchema = z.array(memberInstituteSchema);

export type MemberInstitute = z.infer<typeof memberInstituteSchema>;

export const collectorSchema = z.object({
  userId: z.coerce.number(),
  fullName: z.string().nullable(),
  username: z.string().nullable(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  designationName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  instituteCount: z.number(),
});

export const collectorListSchema = z.array(collectorSchema);

export type Collector = z.infer<typeof collectorSchema>;
